import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, ExternalLink, Loader2, Image as ImageIcon, TableProperties, ChevronDown, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import type { ExampleConfig, ExampleTableRow } from './types';
import { ALL_EVENT_TYPES, EVENT_TYPE_CATEGORIES, EVENT_CATEGORY_METADATA } from '@/components/reports/graphs/types';
import type { DiffsPagesItem, DiffsPagesEvent, EventCategoryFilter } from '@/components/reports/graphs/types';
import { fetchDiffsPages } from '@/utils/diffsAnalyticsAPI';
import { getRecentQuarters } from '@/utils/chartDataTransformers';
import { createSharedLink, buildComparisonDataFromEvent } from '@/utils/sharedLinksUtils';
import type { ExternalEventAnnotations } from '@/types/externalDiffs';
import { useEventIcons, getEventIconUrl } from '@/hooks/useEventIcons';

// Table info for picker
export interface TableOption {
  id: string;
  title: string;
  rowCount: number;
}

interface ExampleConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateExample?: (config: ExampleConfig) => void;
  onCreateExampleTable?: (rows: ExampleTableRow[]) => void;
  onAddToTable?: (row: Omit<ExampleTableRow, 'id'>, tableId?: string) => void;
  availableTables?: TableOption[];
  lastSelectedTableId?: string | null;
  onTableSelected?: (tableId: string | null) => void;
  mode: 'example' | 'example_table';
  reportTitle?: string; // Report title for campaign tracking
}

interface EventTypeOption {
  value: string;
  label: string;
  category: EventCategoryFilter;
  description: string;
}

// Extended DiffsPagesItem with period field
interface DiffWithPeriod extends DiffsPagesItem {
  period: string;
}

export const ExampleConfigModal: React.FC<ExampleConfigModalProps> = ({
  open,
  onOpenChange,
  onCreateExample,
  onCreateExampleTable,
  onAddToTable,
  availableTables = [],
  lastSelectedTableId,
  onTableSelected,
  mode,
  reportTitle,
}) => {
  const [step, setStep] = useState<'eventType' | 'periodRange' | 'events'>('eventType');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventType, setSelectedEventType] = useState<EventTypeOption | null>(null);
  const [selectedStartPeriod, setSelectedStartPeriod] = useState<string | null>(null);
  const [selectedEndPeriod, setSelectedEndPeriod] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<DiffWithPeriod[]>([]);
  const [tablePickerOpen, setTablePickerOpen] = useState<string | null>(null); // event_id when picker is open
  const tablePickerRef = useRef<HTMLDivElement>(null);

  // Fetch event icons from database (with fallback to constants)
  const { data: eventIconsData } = useEventIcons();

  // Close table picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tablePickerRef.current && !tablePickerRef.current.contains(e.target as Node)) {
        setTablePickerOpen(null);
      }
    };
    if (tablePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [tablePickerOpen]);

  // Get available quarters (last 4 years) - memoized to prevent effect re-runs
  const availableQuarters = useMemo(() => getRecentQuarters(16), []);

  // Load saved preferences from localStorage when modal opens
  useEffect(() => {
    if (open) {
      const savedStartPeriod = localStorage.getItem('exampleModal_startPeriod');
      const savedEndPeriod = localStorage.getItem('exampleModal_endPeriod');
      const savedEventType = localStorage.getItem('exampleModal_eventType');
      const savedStep = localStorage.getItem('exampleModal_step') as 'eventType' | 'periodRange' | 'events' | null;

      if (savedStartPeriod && availableQuarters.includes(savedStartPeriod)) {
        setSelectedStartPeriod(savedStartPeriod);
      }
      if (savedEndPeriod && availableQuarters.includes(savedEndPeriod)) {
        setSelectedEndPeriod(savedEndPeriod);
      }
      if (savedEventType) {
        const eventTypeOption = eventTypeOptions.find(e => e.value === savedEventType);
        if (eventTypeOption) {
          setSelectedEventType(eventTypeOption);
        }
      }
      if (savedStep && ['eventType', 'periodRange', 'events'].includes(savedStep)) {
        setStep(savedStep);
      }
    }
  }, [open, availableQuarters]);

  // Auto-fetch events when restoring to events step
  useEffect(() => {
    if (open && step === 'events' && events.length === 0 && selectedEventType && selectedStartPeriod && selectedEndPeriod && !loading) {
      fetchEvents();
    }
  }, [open, step, selectedEventType, selectedStartPeriod, selectedEndPeriod]);

  // Save preferences when they change
  useEffect(() => {
    if (selectedStartPeriod) {
      localStorage.setItem('exampleModal_startPeriod', selectedStartPeriod);
    }
    if (selectedEndPeriod) {
      localStorage.setItem('exampleModal_endPeriod', selectedEndPeriod);
    }
  }, [selectedStartPeriod, selectedEndPeriod]);

  useEffect(() => {
    if (selectedEventType) {
      localStorage.setItem('exampleModal_eventType', selectedEventType.value);
    }
  }, [selectedEventType]);

  useEffect(() => {
    localStorage.setItem('exampleModal_step', step);
  }, [step]);

  // Create event type options from ALL_EVENT_TYPES
  const eventTypeOptions: EventTypeOption[] = ALL_EVENT_TYPES.map(eventType => ({
    value: eventType,
    label: eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    category: EVENT_TYPE_CATEGORIES[eventType],
    description: `${EVENT_CATEGORY_METADATA[EVENT_TYPE_CATEGORIES[eventType]].label} event`
  }));

  // Group by category for better UX
  const groupedByCategory = {
    pricing: eventTypeOptions.filter(e => e.category === 'pricing'),
    packaging: eventTypeOptions.filter(e => e.category === 'packaging'),
    product: eventTypeOptions.filter(e => e.category === 'product')
  };

  const filteredEventTypes = eventTypeOptions.filter(e =>
    e.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEventTypeSelect = (eventType: EventTypeOption) => {
    setSelectedEventType(eventType);
    // Set defaults: use saved periods if available, otherwise all quarters (oldest to newest)
    if (!selectedStartPeriod) {
      setSelectedStartPeriod(availableQuarters[availableQuarters.length - 1]);
    }
    if (!selectedEndPeriod) {
      setSelectedEndPeriod(availableQuarters[0]);
    }
    setStep('periodRange');
  };

  const fetchEvents = async () => {
    if (!selectedEventType || !selectedStartPeriod || !selectedEndPeriod) return;

    setLoading(true);
    setEvents([]);
    setStep('events');

    try {
      // Generate array of periods from start to end
      const startIdx = availableQuarters.indexOf(selectedStartPeriod);
      const endIdx = availableQuarters.indexOf(selectedEndPeriod);

      // Reverse to get chronological order (oldest to newest)
      const periodsInRange = availableQuarters.slice(endIdx, startIdx + 1).reverse();

      // Fetch events for each period in the range
      const allResponses = await Promise.all(
        periodsInRange.map(period =>
          fetchDiffsPages({
            period,
            breakdown_type: 'event_type',
            breakdown_value: selectedEventType.value,
            pagination: { page: 1, per_page: 200 },
            count_mode: 'all',
          })
        )
      );

      // Merge all diffs with their periods
      const allDiffs: DiffWithPeriod[] = [];
      for (const response of allResponses) {
        if (response && response.diffs) {
          const diffsWithPeriod: DiffWithPeriod[] = response.diffs.map(diff => ({
            ...diff,
            period: response.period
          }));
          allDiffs.push(...diffsWithPeriod);
        }
      }

      // Sort by period (most recent first), then by event count, then by company name
      allDiffs.sort((a, b) => {
        const periodDiff = b.period.localeCompare(a.period);
        if (periodDiff !== 0) return periodDiff;
        const eventDiff = b.event_count - a.event_count;
        if (eventDiff !== 0) return eventDiff;
        return a.company_name.localeCompare(b.company_name);
      });

      setEvents(allDiffs);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to extract before/after URLs from compare_viewer_url
  const extractUrlsFromCompareViewer = (compareUrl?: string): { beforeUrl?: string; afterUrl?: string } => {
    if (!compareUrl) return {};

    try {
      const url = new URL(compareUrl);
      const url1 = url.searchParams.get('url1');
      const url2 = url.searchParams.get('url2');

      return {
        beforeUrl: url1 ? decodeURIComponent(url1) : undefined,
        afterUrl: url2 ? decodeURIComponent(url2) : undefined
      };
    } catch (e) {
      console.error('[ExampleConfigModal] Failed to parse compare_viewer_url:', e);
      return {};
    }
  };

  // Helper to extract version labels from compare_viewer_url (label1/label2 params)
  const extractVersionsFromCompareViewer = (compareUrl?: string): { beforeVersion?: string; afterVersion?: string } => {
    if (!compareUrl) return {};

    try {
      const url = new URL(compareUrl);
      const label1 = url.searchParams.get('label1');
      const label2 = url.searchParams.get('label2');

      return {
        beforeVersion: label1 || undefined,
        afterVersion: label2 || undefined
      };
    } catch (e) {
      return {};
    }
  };

  const handleCreateExample = (item: DiffWithPeriod, event: DiffsPagesEvent) => {
    if (mode !== 'example' || !onCreateExample) return;

    // For "after_only" type (like plan_added), we need the unannotated before screenshot and the annotated after
    // For "before_only" type (like plan_removed), we need the annotated before and unannotated after
    // For normal diffs, we use annotated images for both sides
    // Use diff-level URLs as fallbacks when event doesn't have specific images
    // Extract URLs from compare_viewer_url as last resort

    // Try to get URLs from combined_annotation first, or extract from compare_viewer_url
    const diffUrls = item.combined_annotation?.before_viewer_url && item.combined_annotation?.after_viewer_url
      ? {
          beforeUrl: item.combined_annotation.before_viewer_url,
          afterUrl: item.combined_annotation.after_viewer_url
        }
      : extractUrlsFromCompareViewer(item.combined_annotation?.compare_viewer_url || event.annotation?.compare_viewer_url);

    let beforeImage: string | undefined;
    let afterImage: string | undefined;

    if (event.annotation?.type === 'after_only') {
      // Use unannotated before viewer URL (shows state without the new item)
      // Prefer combined_annotation (Cloudinary) over event-level (could be AWS)
      beforeImage = diffUrls.beforeUrl || event.annotation.before_viewer_url;
      // Use annotated after URL to highlight what was added
      afterImage = event.annotation.after_annotated_urls?.[0] || diffUrls.afterUrl || event.annotation.after_viewer_url;
    } else if (event.annotation?.type === 'before_only') {
      // Use annotated before URL to highlight what was removed
      beforeImage = event.annotation.before_annotated_urls?.[0] || diffUrls.beforeUrl || event.annotation.before_viewer_url;
      // Use unannotated after viewer URL (shows state without the removed item)
      // Prefer combined_annotation (Cloudinary) over event-level (could be AWS)
      afterImage = diffUrls.afterUrl || event.annotation.after_viewer_url;
    } else {
      // Normal diff - use annotated images for both sides, fall back to diff-level Cloudinary URLs
      // Prioritize: 1) annotated URLs, 2) combined_annotation viewer URLs (Cloudinary), 3) event viewer URLs (could be AWS)
      beforeImage = event.annotation?.before_annotated_urls?.[0] || diffUrls.beforeUrl || event.annotation?.before_viewer_url;
      afterImage = event.annotation?.after_annotated_urls?.[0] || diffUrls.afterUrl || event.annotation?.after_viewer_url;
    }

    if (!beforeImage || !afterImage) {
      toast.error('Example requires at least one image');
      return;
    }

    // Extract version labels from compare_viewer_url
    const versions = extractVersionsFromCompareViewer(event.annotation?.compare_viewer_url);

    const config: ExampleConfig = {
      caption: event.description,
      beforeImageUrl: beforeImage,
      afterImageUrl: afterImage,
      compareViewerUrl: event.annotation.compare_viewer_url,
      beforeCropCenter: event.annotation.before_marker,
      afterCropCenter: event.annotation.after_marker,
      beforeMarker: event.annotation.before_marker,
      afterMarker: event.annotation.after_marker,
      companySlug: item.company_slug,
      companyName: item.company_name,
      eventType: event.event_type,
      eventId: event.event_id,
      period: item.period,
      beforeVersion: versions.beforeVersion,
      afterVersion: versions.afterVersion,
    };

    onCreateExample(config);
    // Don't call handleClose() - parent handles closing via setExampleConfigModal
    // Calling it here causes race condition with pendingElements cleanup
  };

  const handleAddToTable = async (item: DiffWithPeriod, event: DiffsPagesEvent, tableId?: string) => {
    if (mode !== 'example_table' || !onAddToTable) return;


    let compareViewerUrl = event.annotation?.compare_viewer_url;
    let sharedLinkId: string | undefined;

    // Extract full description from compare_viewer_url caption (more descriptive than event.description)
    // Do this FIRST so we can use it in the link title
    let fullDescription = event.description;
    if (event.annotation?.compare_viewer_url) {
      try {
        const url = new URL(event.annotation.compare_viewer_url);
        const caption = url.searchParams.get('caption');
        if (caption) {
          fullDescription = decodeURIComponent(caption);
        }
      } catch (e) {
        console.warn('[ExampleConfigModal] Could not extract caption from URL:', e);
      }
    }

    // Automatically generate shortened link if annotation data is available
    // Use event.annotation which has cloudinary_urls with ORIGINAL URLs (not annotated)
    if (event.annotation) {
      const comparisonData = buildComparisonDataFromEvent(
        event,
        event.annotation as unknown as ExternalEventAnnotations
      );

      if (comparisonData) {
        // Get table name from tableId lookup
        const tableName = tableId
          ? availableTables.find(t => t.id === tableId)?.title
          : undefined;

        const result = await createSharedLink({
          comparison_data: comparisonData,
          title: `${item.company_name} - ${fullDescription}`,  // Use full description in title
          campaign_source: 'report_builder',
          campaign_medium: tableName || 'unknown_table',
          campaign_name: reportTitle || 'untitled_report',
        });

        if ('url' in result) {
          compareViewerUrl = result.url;  // Replace with shortened URL
          sharedLinkId = result.id;  // Store for future updates
        } else {
          console.error('[ExampleConfigModal] ❌ Failed to create shared link:', result.error);
          toast.error(`Link generation failed: ${result.error || 'Unknown error'}`);
          // Fallback to original compare_viewer_url
        }
      } else {
        console.warn('[ExampleConfigModal] ⚠️ buildComparisonDataFromEvent returned null');
        toast.warning('Could not build comparison data for link');
      }
    } else {
      console.warn('[ExampleConfigModal] ⚠️ No event.annotation available');
    }

    const row: Omit<ExampleTableRow, 'id'> = {
      period: item.period,
      companyName: item.company_name,
      companySlug: item.company_slug,
      eventType: event.event_type,
      description: fullDescription,  // Use caption from URL if available
      compareViewerUrl,  // Now contains shortened URL
      sharedLinkId,  // Store link ID for updating when names change
      eventId: event.event_id,
      annotation: event.annotation,  // Store for link regeneration
      combined_annotation: item.combined_annotation,  // Store diff-level annotation
      icon_url: getEventIconUrl(event.event_type, eventIconsData),  // Assign icon from DB or fallback
    };

    onAddToTable(row, tableId);
    // Don't close modal - allow adding multiple items
    toast.success(tableId ? `Added to table` : 'Table created with example');
  };

  const handleClose = () => {
    setStep('eventType');
    setSearchQuery('');
    setSelectedEventType(null);
    setSelectedStartPeriod(null);
    setSelectedEndPeriod(null);
    setEvents([]);
    setTablePickerOpen(null);
    onOpenChange(false);
  };

  const formatEventType = (eventType: string) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Group events by quarter
  const eventsByQuarter = events.reduce((acc, item) => {
    const quarter = item.period || 'Unknown';
    if (!acc[quarter]) {
      acc[quarter] = [];
    }
    acc[quarter].push(item);
    return acc;
  }, {} as Record<string, DiffWithPeriod[]>);

  const sortedQuarters = Object.keys(eventsByQuarter).sort().reverse(); // Most recent first

  // Get dialog title based on step
  const getDialogTitle = () => {
    if (step === 'eventType') return 'Select Event Type';
    if (step === 'periodRange') return `Select Period Range for ${selectedEventType?.label}`;
    return `Events: ${selectedEventType?.label}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="relative">
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>
            {step === 'eventType' && 'Select an event type to find examples'}
            {step === 'periodRange' && 'Choose the time range for events'}
            {step === 'events' && 'Browse events and create examples'}
          </DialogDescription>
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-8 w-8 p-0"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        {step === 'eventType' && (
          <div className="flex-1 overflow-y-auto">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search event types..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {searchQuery ? (
              // Show filtered results when searching
              <div className="space-y-2">
                {filteredEventTypes.map((eventType) => (
                  <button
                    key={eventType.value}
                    onClick={() => handleEventTypeSelect(eventType)}
                    className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors text-left"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{eventType.label}</div>
                      <div className="text-xs text-gray-500">{eventType.description}</div>
                    </div>
                  </button>
                ))}
                {filteredEventTypes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No event types found
                  </div>
                )}
              </div>
            ) : (
              // Show grouped by category when not searching
              <div className="space-y-4">
                {/* Pricing Events */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Pricing Events
                  </h3>
                  <div className="space-y-1">
                    {groupedByCategory.pricing.map((eventType) => (
                      <button
                        key={eventType.value}
                        onClick={() => handleEventTypeSelect(eventType)}
                        className="w-full text-left p-3 border rounded-lg hover:bg-green-50 hover:border-green-500 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{eventType.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Packaging Events */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    Packaging Events
                  </h3>
                  <div className="space-y-1">
                    {groupedByCategory.packaging.map((eventType) => (
                      <button
                        key={eventType.value}
                        onClick={() => handleEventTypeSelect(eventType)}
                        className="w-full text-left p-3 border rounded-lg hover:bg-amber-50 hover:border-amber-500 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{eventType.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Product Events */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-indigo-500"></span>
                    Product Events
                  </h3>
                  <div className="space-y-1">
                    {groupedByCategory.product.map((eventType) => (
                      <button
                        key={eventType.value}
                        onClick={() => handleEventTypeSelect(eventType)}
                        className="w-full text-left p-3 border rounded-lg hover:bg-indigo-50 hover:border-indigo-500 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{eventType.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Period Range Selection */}
        {step === 'periodRange' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Period
                </label>
                <select
                  value={selectedStartPeriod || ''}
                  onChange={(e) => setSelectedStartPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availableQuarters.map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Period
                </label>
                <select
                  value={selectedEndPeriod || ''}
                  onChange={(e) => setSelectedEndPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availableQuarters.map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p className="font-medium mb-1">Selected Range:</p>
                <p>
                  {selectedStartPeriod} to {selectedEndPeriod}
                </p>
              </div>

              <Button onClick={fetchEvents} className="w-full" size="lg">
                Continue to Events
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Events Display */}
        {step === 'events' && (
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No events found for this event type in the selected period range</p>
                <p className="text-sm mt-2">Try selecting a different period range or event type</p>
              </div>
            ) : (
              <div className="space-y-6">
                {sortedQuarters.map((quarter) => (
                  <div key={quarter} className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-2 rounded sticky top-0 z-10">
                      {quarter}
                    </h3>
                    <div className="space-y-3">
                      {eventsByQuarter[quarter].map((item) => (
                        <div
                          key={item.diff_id}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start gap-3 mb-3">
                            {item.logo_url && (
                              <img
                                src={item.logo_url}
                                alt={item.company_name}
                                className="w-10 h-10 rounded object-contain flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900">{item.company_name}</div>
                              <div className="text-sm text-gray-600">{item.page_name}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {item.event_count} {item.event_count === 1 ? 'event' : 'events'}
                              </div>
                            </div>
                          </div>

                          {/* Show events for this diff */}
                          <div className="space-y-2 ml-0">
                            {item.events.map((event) => (
                              <div
                                key={event.event_id}
                                className="flex items-start justify-between gap-3 p-2 bg-gray-50 rounded"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                      {formatEventType(event.event_type)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700">{event.description}</p>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {event.annotation?.compare_viewer_url && (
                                    <a
                                      href={event.annotation.compare_viewer_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                      title="View comparison"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}

                                  {mode === 'example' && (() => {
                                    const beforeImage = event.annotation?.before_annotated_urls?.[0] || event.annotation?.before_viewer_url;
                                    const afterImage = event.annotation?.after_annotated_urls?.[0] || event.annotation?.after_viewer_url;
                                    const hasImage = beforeImage || afterImage;

                                    return (
                                      <button
                                        onClick={() => handleCreateExample(item, event)}
                                        disabled={!hasImage}
                                        className={`text-xs flex items-center gap-1 px-2 py-1 rounded ${
                                          hasImage
                                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                        title={hasImage ? 'Create visual example' : 'No images available'}
                                      >
                                        <ImageIcon className="w-3 h-3" />
                                        Example
                                      </button>
                                    );
                                  })()}

                                  {mode === 'example_table' && onAddToTable && (
                                    <div className="relative" ref={tablePickerOpen === event.event_id ? tablePickerRef : undefined}>
                                      <div className="flex items-center">
                                        {/* Quick add to last selected table */}
                                        {lastSelectedTableId && availableTables.find(t => t.id === lastSelectedTableId) ? (
                                          <button
                                            onClick={() => {
                                              handleAddToTable(item, event, lastSelectedTableId);
                                            }}
                                            className="text-xs text-teal-600 hover:underline flex items-center gap-1 max-w-[150px]"
                                            title={`Add to "${availableTables.find(t => t.id === lastSelectedTableId)?.title}"`}
                                          >
                                            <TableProperties className="w-3 h-3 flex-shrink-0" />
                                            <span className="truncate">
                                              + {availableTables.find(t => t.id === lastSelectedTableId)?.title || 'Table'}
                                            </span>
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => setTablePickerOpen(tablePickerOpen === event.event_id ? null : event.event_id)}
                                            className="text-xs text-teal-600 hover:underline flex items-center gap-1"
                                            title="Add to table"
                                          >
                                            <TableProperties className="w-3 h-3 flex-shrink-0" />
                                            <span>+ Add to Table</span>
                                          </button>
                                        )}
                                        {lastSelectedTableId && availableTables.find(t => t.id === lastSelectedTableId) && (
                                          <button
                                            onClick={() => setTablePickerOpen(tablePickerOpen === event.event_id ? null : event.event_id)}
                                            className="text-xs text-teal-600 hover:underline flex items-center gap-0.5 ml-0.5"
                                            title="Choose table"
                                          >
                                            <ChevronDown className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                      {/* Table picker dropdown */}
                                      {tablePickerOpen === event.event_id && (
                                        <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 min-w-[180px] py-1">
                                          {availableTables.length > 0 && (
                                            <>
                                              <div className="px-2 py-1 text-xs font-medium text-gray-500 border-b">Add to existing table</div>
                                              {availableTables.map(table => (
                                                <button
                                                  key={table.id}
                                                  onClick={() => {
                                                    handleAddToTable(item, event, table.id);
                                                    onTableSelected?.(table.id);
                                                    setTablePickerOpen(null);
                                                  }}
                                                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center justify-between"
                                                >
                                                  <span className="truncate">{table.title}</span>
                                                  <span className="text-gray-400 ml-2">({table.rowCount})</span>
                                                </button>
                                              ))}
                                              <div className="border-t my-1" />
                                            </>
                                          )}
                                          <button
                                            onClick={() => {
                                              handleAddToTable(item, event, undefined); // undefined = create new table
                                              setTablePickerOpen(null);
                                            }}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 text-teal-600 font-medium flex items-center gap-1"
                                          >
                                            <Plus className="w-3 h-3" />
                                            Create new table
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'periodRange' && (
            <Button variant="outline" onClick={() => setStep('eventType')}>
              Back
            </Button>
          )}
          {step === 'events' && (
            <Button variant="outline" onClick={() => setStep('periodRange')}>
              Back
            </Button>
          )}
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
